
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import  themen  from 'theme/styles/styled-colors';
import  * as theme  from 'react-syntax-highlighter/dist/esm/styles/hljs';
import SyntaxHighlighter from 'react-syntax-highlighter';


const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <h2 {...{
      "id": "building--installing-apache-atlas"
    }}>{`Building & Installing Apache Atlas`}</h2>
    <h3 {...{
      "id": "building-apache-atlas"
    }}>{`Building Apache Atlas`}</h3>
    <p>{`Download Apache Atlas 1.0.0 release sources, apache-atlas-1.0.0-sources.tar.gz, from the `}<a parentName="p" {...{
        "href": "#/Downloads"
      }}>{`downloads`}</a>{` page.
Then follow the instructions below to to build Apache Atlas.`}</p>
    <SyntaxHighlighter wrapLines={true} language="powershell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`tar xvfz apache-atlas-1.0.0-sources.tar.gz
cd apache-atlas-sources-1.0.0/
export MAVEN_OPTS="-Xms2g -Xmx2g"
mvn clean -DskipTests install`}
    </SyntaxHighlighter>
    <h3 {...{
      "id": "packaging-apache-atlas"
    }}>{`Packaging Apache Atlas`}</h3>
    <p>{`To create Apache Atlas package for deployment in an environment having functional Apache HBase and Apache Solr instances, build with the following command:`}</p>
    <SyntaxHighlighter wrapLines={true} language="powershell" style={theme.dark} mdxType="SyntaxHighlighter">
mvn clean -DskipTests package -Pdist
    </SyntaxHighlighter>
    <ul>
      <li parentName="ul">{`NOTES:`}
        <ul parentName="li">
          <li parentName="ul">{`Remove option '-DskipTests' to run unit and integration tests`}</li>
          <li parentName="ul">{`To build a distribution without minified js,css file, build with `}<em parentName="li">{`skipMinify`}</em>{` profile. By default js and css files are minified.`}</li>
        </ul>
      </li>
    </ul>
    <p>{`Above will build Apache Atlas for an environment having functional HBase and Solr instances. Apache Atlas needs to be setup with the following to run in this environment:`}</p>
    <ul>
      <li parentName="ul">{`Configure atlas.graph.storage.hostname (see "Graph persistence engine - HBase" in the `}<a parentName="li" {...{
          "href": "#/Configuration"
        }}>{`Configuration`}</a>{` section).`}</li>
      <li parentName="ul">{`Configure atlas.graph.index.search.solr.zookeeper-url (see "Graph Search Index - Solr" in the `}<a parentName="li" {...{
          "href": "#/Configuration"
        }}>{`Configuration`}</a>{` section).`}</li>
      <li parentName="ul">{`Set HBASE_CONF_DIR to point to a valid Apache HBase config directory (see "Graph persistence engine - HBase" in the `}<a parentName="li" {...{
          "href": "#/Configuration"
        }}>{`Configuration`}</a>{` section).`}</li>
      <li parentName="ul">{`Create indices in Apache Solr (see "Graph Search Index - Solr" in the `}<a parentName="li" {...{
          "href": "#/Configuration"
        }}>{`Configuration`}</a>{` section).`}</li>
    </ul>
    <h3 {...{
      "id": "packaging-apache-atlas-with-embedded-apache-hbase--apache-solr"
    }}>{`Packaging Apache Atlas with embedded Apache HBase & Apache Solr`}</h3>
    <p>{`To create Apache Atlas package that includes Apache HBase and Apache Solr, build with the embedded-hbase-solr profile as shown below:`}</p>
    <SyntaxHighlighter wrapLines={true} language="powershell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`mvn clean -DskipTests package -Pdist,embedded-hbase-solr`}
    </SyntaxHighlighter>
    <p>{`Using the embedded-hbase-solr profile will configure Apache Atlas so that an Apache HBase instance and an Apache Solr instance will be started and stopped along with the Apache Atlas server.`}</p>
    <blockquote>
      <p parentName="blockquote">{`NOTE: This distribution profile is only intended to be used for single node development not in production.`}</p>
    </blockquote>
    <h3 {...{
      "id": "packaging-apache-atlas-with-berkeleydb--apache-solr"
    }}>{`Packaging Apache Atlas with BerkeleyDB & Apache Solr`}</h3>
    <p>{`To create Apache Atlas package that includes BerkeleyDB and Apache Solr, build with the berkeley-solr profile as shown below:`}</p>
    <SyntaxHighlighter wrapLines={true} language="powershell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`mvn clean -DskipTests package -Pdist,berkeley-solr`}
    </SyntaxHighlighter>
    <p>{`Using the berkeley-solr profile will configure Apache Atlas so that instances of Apache Solr and Apache Zookeeper will be started and stopped along with the Apache Atlas server.`}</p>
    <blockquote>
      <p parentName="blockquote">{`NOTE: This distribution profile is only intended to be used for single node development not in production.`}</p>
    </blockquote>
    <h3 {...{
      "id": "packaging-apache-atlas-with-embedded-apache-cassandra--apache-solr"
    }}>{`Packaging Apache Atlas with embedded Apache Cassandra & Apache Solr`}</h3>
    <p>{`To create Apache Atlas package that includes Apache Cassandra and Apache Solr, build with the embedded-cassandra-solr profile as shown below:`}</p>
    <SyntaxHighlighter wrapLines={true} language="powershell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`mvn clean package -Pdist,embedded-cassandra-solr`}
    </SyntaxHighlighter>
    <p>{`Using the embedded-cassandra-solr profile will configure Apache Atlas so that an Apache Cassandra instance and an Apache Solr instance will be started and stopped along with the Atlas server.`}</p>
    <blockquote>
      <p parentName="blockquote">{`NOTE: This distribution profile is only intended to be used for single node development not in production.`}</p>
    </blockquote>
    <h3 {...{
      "id": "apache-atlas-package"
    }}>{`Apache Atlas Package`}</h3>
    <p>{`Build will create following files, which are used to install Apache Atlas.`}</p>
    <SyntaxHighlighter wrapLines={true} language="powershell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`distro/target/apache-atlas-{project.version}-bin.tar.gz
distro/target/apache-atlas-{project.version}-hbase-hook.tar.gz
distro/target/apache-atlas-{project.version}-hive-hook.gz
distro/target/apache-atlas-{project.version}-kafka-hook.gz
distro/target/apache-atlas-{project.version}-sources.tar.gz
distro/target/apache-atlas-{project.version}-sqoop-hook.tar.gz
distro/target/apache-atlas-{project.version}-storm-hook.tar.gz`}
    </SyntaxHighlighter>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;